/* istanbul ignore file */
/* ported from components */
import 'intersection-observer';

import React from 'react';
import { LazyImage } from 'react-lazy-images';
import NextImage from 'next/image';

import { imageLoader } from '@lib/image';

import PlaceholderImage from './PlaceholderImage';
import styles from './Image.module.scss';
import { defaultProps, IProps } from './Image.props';

const makeThumborUrl = (options: IProps) => {
  const {
    server,
    src,
    width,
    height,
    trim,
    fitIn,
    hmac,
    filters = {},
  } = options;

  const urlParts = [server];

  if (hmac) {
    urlParts.push('hmac');
  }

  if (trim) {
    if (typeof trim === 'string') {
      urlParts.push(`trim:${trim}`);
    } else {
      urlParts.push('trim');
    }
  }

  if (fitIn) {
    urlParts.push('fit-in');
  }

  if (width || height) {
    urlParts.push(`${width || ''}x${height || ''}`);
  }

  const filterKeys = Object.keys(filters);
  if (filterKeys.length) {
    const formatted = filterKeys.map((key: string) => {
      const value: any = filters[key];

      return `${key}(${value || ''})`;
    });
    urlParts.push(`filters:${formatted.join(':')}`);
  }

  urlParts.push(src);
  const url = urlParts.join('/');

  return url;
};

const Image: React.FC<IProps> = (props: IProps) => {
  const {
    className,
    style,
    id,
    alt,
    src,
    srcSet,
    loadEagerly = false,
    nextImage = false,
    priority = false,
    sizes = '100vw',
    quality = 50,
    onLoad = () => {}
  } = props;

  const imgProps: {[key: string]: any} = {
    src: makeThumborUrl(props),
  };

  if (srcSet) {
    const augmentedFilters = (size: number) => ({
      ...props.filters,
      proportion: size,
    });

    imgProps.srcSet = srcSet.map((size: number) => {
      return `${makeThumborUrl({ ...props, filters: augmentedFilters(size) })} ${size}x`;
    }).join(', ');
  }

  return (
    <LazyImage
      alt={alt}
      src={imgProps.src}
      srcSet={imgProps.srcSet}
      placeholder={({ imageProps, ref }) => (
        <PlaceholderImage
          alt={imageProps.alt || ''}
          className={className}
          ref={ref}
          style={style}
        />
      )}
      actual={({ imageProps }) => {
        if (nextImage) {
          return (
            <NextImage
              alt={imageProps.alt}
              layout="fill"
              priority={priority}
              quality={quality}
              sizes={sizes}
              src={src}
              loader={imageLoader}
            />
          );
        }
        return (
          <img
            {...imageProps}
            alt={imageProps.alt}
            id={id}
            className={`${styles['a-image-loaded']} ${className || ''}`}
            style={style}
            onLoad={onLoad}
          />
        );
      }}
      loadEagerly={ loadEagerly }
    />
  );
};

Image.defaultProps = defaultProps;

export default React.memo(Image);
